import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'focus-visible:ring-ring inline-flex cursor-pointer items-center justify-center gap-2 rounded-full text-sm leading-none font-medium tracking-normal whitespace-nowrap transition-colors duration-500 focus-visible:ring-1 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'border-forground text-background bg-foreground hover:bg-background hover:text-foreground hover:border-foreground border shadow',
        primary:
          'border-primary text-primary-foreground bg-primary hover:bg-background hover:text-primary border shadow',
        secondary:
          'border-primary bg-background text-primary hover:bg-primary hover:text-primary-foreground border shadow',
        destructive:
          'border-destructive bg-destructive text-destructive-foreground hover:bg-background hover:text-destructive border shadow-xs',
        outline:
          'border-ghost text-ghost hover:bg-ghost border shadow hover:text-white',
        ghost:
          'hover:bg-accent hover:text-accent-foreground active:bg-black/20 active:duration-200',
        link: 'text-primary font-semibold underline-offset-1 hover:underline dark:text-white',
        'primary-outline':
          'border-primary-fore text-primary-fore hover:border-primary hover:bg-primary hover:text-primary-foreground bg-s-200 bg-pos-0 border shadow',
        cover: 'text-primary-foreground bg-black/75',
        special:
          'gradient-br-via bg-pos-0 hover:bg-pos-100 bg-size-200 border-0 text-white transition-all',
      },
      size: {
        link: 'font-medium',
        default: 'px-6 py-3 font-semibold',
        xs: 'h-6 px-3 text-xs',
        sm: 'h-8 px-4 text-xs',
        // マイページの「変更」などに使う 38px の枠線ボタン（Figma 指定）
        md: 'h-9.5 min-w-20 border-2 px-4 font-plus text-sm leading-none font-bold tracking-[0.7px] shadow-none',
        lg: 'h-10 px-8 font-semibold',
        xl: 'h-12 px-12 text-base font-semibold md:px-18',
        icons: 'h-9 w-9 font-semibold',
        icon: 'h-12 w-12 font-semibold',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className, variant, size, asChild = false, ...props
  }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
